import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  TrendingUp,
  Users,
  CreditCard,
  FileBarChart,
  AlertTriangle,
  DollarSign,
} from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
  toolsUsed?: string[];
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  { icon: TrendingUp, label: "Monthly savings summary", question: "What are the total savings for this month?" },
  { icon: Users, label: "Members missing declarations", question: "Which members haven't declared their shares this month?" },
  { icon: CreditCard, label: "Loan overview", question: "What is the current loan status? Show me active loans and outstanding amounts." },
  { icon: AlertTriangle, label: "At-risk loans", question: "Are there any overdue loans or loans approaching their due date?" },
  { icon: FileBarChart, label: "Monthly report", question: "Generate a comprehensive report for this month covering savings, loans, and expenses." },
  { icon: DollarSign, label: "Expense breakdown", question: "What are the main expense categories this month?" },
];

export default function AIAssistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMessage: Message = {
      role: "user",
      content: text.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const history = messages.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await api.post("/ai/chat", {
        message: text.trim(),
        history,
      });

      const assistantMessage: Message = {
        role: "assistant",
        content: response.data.data.content,
        toolsUsed: response.data.data.toolsUsed,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        role: "assistant",
        content:
          error.response?.data?.message ||
          "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const formatContent = (content: string) => {
    // Simple markdown-like formatting
    return content
      .split("\n")
      .map((line, i) => {
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        // Inline code
        line = line.replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded text-sm">$1</code>');
        // Bullet points
        if (line.startsWith("- ")) {
          return `<li class="ml-4">${line.slice(2)}</li>`;
        }
        return line;
      })
      .join("\n");
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#009A44] flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold">AI Assistant</h1>
          <p className="text-sm text-muted-foreground">
            Ask questions about your cooperative's data
          </p>
        </div>
        <Badge variant="secondary" className="ml-auto">
          <Sparkles className="w-3 h-3 mr-1" />
          AI-Powered
        </Badge>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardContent className="flex-1 flex flex-col p-0">
          <ScrollArea ref={scrollRef} className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#009A44]/10 flex items-center justify-center mb-4">
                  <Bot className="w-8 h-8 text-[#009A44]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">How can I help you?</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md">
                  I can help you analyze savings, track loan performance, generate reports,
                  and answer questions about your cooperative's data.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-w-2xl">
                  {SUGGESTED_QUESTIONS.map((sq, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      className="justify-start h-auto py-3 px-4 text-left"
                      onClick={() => sendMessage(sq.question)}
                      disabled={isLoading}
                    >
                      <sq.icon className="w-4 h-4 mr-2 shrink-0 text-[#009A44]" />
                      <span className="text-sm">{sq.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-[#009A44] text-white"
                          : "bg-slate-100 text-slate-900"
                      }`}
                    >
                      {msg.role === "assistant" && (
                        <div className="flex items-center gap-1 mb-1">
                          <Bot className="w-3 h-3" />
                          <span className="text-xs font-medium opacity-70">AI Assistant</span>
                        </div>
                      )}
                      <div
                        className="text-sm whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
                      />
                      {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {msg.toolsUsed.map((tool, j) => (
                            <Badge
                              key={j}
                              variant="secondary"
                              className="text-[10px] bg-white/20 text-white border-0"
                            >
                              {tool.replace(/_/g, " ")}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-lg px-4 py-3 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#009A44]" />
                      <span className="text-sm text-muted-foreground">Thinking...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="border-t p-4">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about savings, loans, members, reports..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="bg-[#009A44] hover:bg-[#008238] text-white"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
